import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validator de grupo do Controle de Múltiplo do produto.
 *
 * Os campos são opcionais, mas **pareados**: com `isControlaMultiplo` ligado, tanto a
 * `idUnidadeMultiplo` quanto o `fatorMultiplo` (maior que zero) passam a ser obrigatórios.
 *
 * A regra "múltiplo não pode ser da mesma unidade do produto" **não** é revalidada aqui: o combo de
 * `produto-outros` já não oferece a unidade de medida do produto e limpa a seleção se ela deixar de
 * ser válida; a barreira de verdade é o backend (`ProdutoController.ValidarControleMultiplo`).
 * Atenção ao comparar com o {@link fatorConversaoValidator} vizinho: lá a igualdade entre unidades é
 * o caso trivial permitido, aqui ela é proibida — não copiar aquela condição por analogia.
 *
 * Deve ser aplicado no **FormGroup raiz**:
 * ```ts
 * this.form = this.fb.group({ ... }, { validators: [fatorConversaoValidator(), controleMultiploValidator()] });
 * ```
 */
export function controleMultiploValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
        const isControlaMultiplo = group.get('isControlaMultiplo')?.value === true;
        const unidadeControl = group.get('idUnidadeMultiplo');
        const fatorControl = group.get('fatorMultiplo');

        if (!isControlaMultiplo) {
            limparRequired(unidadeControl);
            limparRequired(fatorControl);
            return null;
        }

        const unidade = unidadeControl?.value;
        const fator = fatorControl?.value;

        const unidadeVazia = unidade === null || unidade === undefined || unidade === '';
        const fatorInvalido = fator === null || fator === undefined || fator === '' || Number(fator) <= 0;

        if (unidadeVazia) {
            unidadeControl?.setErrors({ required: true });
        } else {
            limparRequired(unidadeControl);
        }

        if (fatorInvalido) {
            fatorControl?.setErrors({ required: true });
        } else {
            limparRequired(fatorControl);
        }

        if (unidadeVazia || fatorInvalido) {
            return { controleMultiploIncompleto: true };
        }

        return null;
    };
}

/**
 * Remove só o erro `required` do control, preservando os demais que possam existir
 * (mesma abordagem do fatorConversaoValidator).
 */
function limparRequired(control: AbstractControl | null): void {
    const errosAtuais = control?.errors;
    if (!errosAtuais?.['required']) return;

    const { required, ...restErrors } = errosAtuais;
    control?.setErrors(Object.keys(restErrors).length ? restErrors : null);
}
